/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import styled from '@emotion/styled'
import { Chip, Grid, Typography } from '@mui/material'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { rgbaStrOpacity } from 'src/utils/colors'
import { SUBSCRIPTION_TIERS } from 'src/constants/subscriptions'
import { ProductWithPrices } from 'src/types/payments'
import { formatVal } from 'src/utils/number-format'

import * as paymentsAtoms from 'src/atoms/payments'
import * as billingAtoms from 'src/atoms/billing'
import * as userAtoms from 'src/atoms/user'

export const COLUMN_WIDTH = {
  primary: 12 - 9,
  other: 9 / 4,
}

// Header
const Header = styled(Grid)`
  width: 100%;
  background-color: ${Colors.neutral50};
`
const HeaderItem = styled(Grid)`
  display: flex;
  flex-direction: column;
  min-height: 165px;
  background-color: ${Colors.neutral50};
  border-right: 1px solid ${Colors.border};
  ${useTypography(Typographies.title1)}
  :last-of-type {
    border-right: none;
  }
`
const FeatureColumn = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: ${Shapes.spacing[4]}px;
`
const TierColumn = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${Shapes.spacing[4]}px;
  padding: ${Shapes.spacing[4]}px;
`
const TierMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[4]}px;
`
const TierName = styled(Typography)`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[2]}px;
  height: ${Shapes.spacing[6]}px;
`
const TierNameChip = styled(Chip)`
  padding: ${Shapes.spacing[1] / 2}px ${Shapes.spacing[1]}px;
  color: ${Colors.blueDark};
  background-color: ${rgbaStrOpacity(Colors.blueDark, 0.25)};
  text-transform: uppercase;
  ${useTypography(Typographies.textmd)}
`
const TierPrice = styled(Typography)`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: ${Shapes.spacing[1]}px;
  span:last-of-type {
    ${useTypography(Typographies.textmdm)}
    color: ${Colors.textMedium};
  }
`
const TierPriceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[1]}px;
`
const TierButton = styled(ButtonWithIcon)`
  flex-shrink: 0;
  height: ${Shapes.spacing[7]}px;
  ${useTypography(Typographies.title2)}
  width: 100%;
  :disabled {
    background-color: ${Colors.neutral100} !important;
    color: ${Colors.textLight} !important;
    border: none;
  }
`
const SlashedPrice = styled.span`
  color: ${Colors.textDisabled};
  text-decoration: line-through;
`

export type SubscriptionTableHeaderProps = {
  onUpgrade: () => void
  onDowngrade: () => void
}
export const SubscriptionTableHeader = (props: SubscriptionTableHeaderProps) => {
  const { onUpgrade, onDowngrade } = props
  const subscriptionInterval = useRecoilValue(paymentsAtoms.subscriptionInterval)
  const paymentPlans = useRecoilValue(paymentsAtoms.paymentPlans)
  const setSelectedPaymentPlan = useSetRecoilState(paymentsAtoms.selectedPaymentPlan)
  const currentUser = useRecoilValue(userAtoms.currentUser)
  const subscription = useRecoilValue(billingAtoms.subscription)

  const onUpgradeClick = (plan: ProductWithPrices) => () => {
    setSelectedPaymentPlan(plan)
    onUpgrade()
  }

  const onDowngradeClick = (plan: ProductWithPrices) => () => {
    setSelectedPaymentPlan(plan)
    onDowngrade()
  }

  const currentTierIndex = useMemo(() => {
    if (!currentUser || (subscription && subscription.status === 'canceled')) {
      return -1
    }
    return SUBSCRIPTION_TIERS.findIndex((tier) => tier === currentUser.tier) + 1
  }, [currentUser, subscription])

  const headers = useMemo(() => {
    const headers = [
      {
        name: 'Included Features',
        xs: COLUMN_WIDTH.primary,
        monthlyPrice: undefined,
        annualPrice: undefined,
        annualizedMonthlyPrice: undefined,
        occurrence: undefined,
        plan: undefined,
      },
      {
        name: 'Free',
        xs: COLUMN_WIDTH.other,
        price: 0,
        monthlyPrice: 0,
        annualPrice: 0,
        annualizedMonthlyPrice: 0,
        occurrence: `${subscriptionInterval === 'month' ? 'mo' : 'yr'}`,
        plan: undefined,
      },
      ...paymentPlans.map((plan) => ({
        name: plan.name,
        xs: COLUMN_WIDTH.other,
        monthlyPrice: Math.floor(
          (plan.prices.find((price) => price.recurring?.interval === 'month')?.unit_amount || 0) / 100
        ),
        annualPrice: Math.floor(
          (plan.prices.find((price) => price.recurring?.interval === 'year')?.unit_amount || 0) / 100
        ),
        annualizedMonthlyPrice: Math.floor(
          (plan.prices.find((price) => price.recurring?.interval === 'year')?.unit_amount || 0) / 12 / 100
        ),
        occurrence: `${subscriptionInterval === 'month' ? 'mo' : 'yr'}`,
        plan,
      })),
    ]

    return headers.map((header, index) => {
      if (index === 0) {
        return (
          <HeaderItem key={header.name} xs={header.xs}>
            <FeatureColumn>{header.name}</FeatureColumn>
          </HeaderItem>
        )
      }
      return (
        <HeaderItem key={header.name} xs={header.xs}>
          <TierColumn>
            <TierMain>
              <TierName variant="header3s">
                {header.name}
                {index === 4 && <TierNameChip label="Most Popular" />}
              </TierName>
              <TierPrice variant="header2" color={Colors.textBlack}>
                {!!header.monthlyPrice ? (
                  <>
                    <TierPriceHeader>
                      {subscriptionInterval === 'year' && (
                        <SlashedPrice>{formatVal(header.monthlyPrice, 'currency')} </SlashedPrice>
                      )}
                      {formatVal(
                        subscriptionInterval === 'year' ? header.annualizedMonthlyPrice : header.monthlyPrice,
                        'currency'
                      )}
                      <span>/ mo</span>
                    </TierPriceHeader>
                    {subscriptionInterval === 'year' && (
                      <div>
                        <Typography variant="textmdm" color={Colors.textLight}>
                          Billed ${formatVal(header.annualPrice, 'number')} annually
                        </Typography>
                      </div>
                    )}
                  </>
                ) : (
                  <TierPriceHeader>
                    $0 <span>/ mo</span>
                  </TierPriceHeader>
                )}
              </TierPrice>
            </TierMain>

            {index === currentTierIndex ? (
              <TierButton inverted disabled>
                Current Plan
              </TierButton>
            ) : index === 1 ? (
              <TierButton disabled>Free</TierButton>
            ) : index < currentTierIndex ? (
              <TierButton onClick={header.plan && onDowngradeClick(header.plan)}>Downgrade</TierButton>
            ) : (
              <TierButton inverted onClick={header.plan && onUpgradeClick(header.plan)}>
                Upgrade
              </TierButton>
            )}
          </TierColumn>
        </HeaderItem>
      )
    })
  }, [currentTierIndex, subscriptionInterval])

  return <Header container>{headers}</Header>
}
